// The builder (DEVELOPMENT.md: Builder; mockup 07): one screen with three levels of help on the
// generator's engine. Desktop: constraint palette on the left, the grid in the middle, Assist,
// Rules, Solve path and Finish message on the right. Phone: the same parts stacked, for light
// edits and the automatic mode. The draft saves itself as the setter works.
import {
  type AutoCandidate,
  type CompleteResult,
  type ConstraintEntry,
  type Draft,
  type HelpLevel,
  type NewDraftResponse,
  type PuzzleDoc,
  type Rc,
  type ReadyResult,
  type AutoResult,
  shapeOf,
  shapeOfGrid,
  type Suggestion,
} from '@zinc/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Splash } from '../components/AppShell';
import { AssistPanel, type AssistState } from '../components/builder/AssistPanel';
import { FogPanel, FogTools, type FogTool, type FogView } from '../components/builder/FogPanel';
import {
  Candidates,
  DrawingPath,
  FogOverlay,
  MarkedPieces,
  RegionCells,
  StallShade,
  type Marked,
} from '../components/builder/Overlays';
import { Palette } from '../components/builder/Palette';
import { PieceCard } from '../components/builder/PieceCard';
import { FinishPanel, PathPanel, RulesPanel } from '../components/builder/SidePanels';
import { BackIcon } from '../components/icons';
import { Grid, pointOf } from '../components/solve/Grid';
import { Menu, menuIcons } from '../components/solve/Menu';
import { Overview } from '../components/solve/Overview';
import { ZoomView } from '../components/solve/ZoomView';
import { DeleteIcon, DotsIcon, RedoIcon, RulesIcon, UndoIcon } from '../components/solve/icons';
import { gridFrame } from '../components/solve/constraints';
import { CELL } from '../components/solve/constraints/geom';
import { api } from '../lib/api';
import { fogModel, hasRc, NEW_FOG, paintCells, withFog, type FogModel } from '../lib/builder/fog';
import {
  useAutosave,
  useBuildJob,
  useBuildReport,
  useFogReport,
  startJob,
} from '../lib/builder/hooks';
import {
  addEntry,
  allKeys,
  amend,
  areaEntry,
  arrowEntry,
  byApp,
  byHand,
  cellOfPt,
  clueEntryAt,
  cloneEntry,
  commit,
  cornerAt,
  diagonalsFrom,
  edgeAt,
  edgeEntryAt,
  entriesAtCell,
  entryKey,
  extendArea,
  extendPath,
  givenAt,
  givenKey,
  lineEntry,
  lockedCount,
  outsideAt,
  outsideEntry,
  paintRegion,
  prune,
  redo,
  regionProblem,
  removablePieces,
  removeEntries,
  same,
  setGivens,
  setGlobal,
  setNegative,
  squareEntry,
  startTimeline,
  toggleCellMark,
  undo,
  updateEntry,
  edgeEntry,
  type Pt,
  type Timeline,
  type Work,
} from '../lib/builder/model';
import { AUTO_GLOBALS, paletteItem, variantOf } from '../lib/builder/palette';
import { core } from '../lib/core/client';
import { emptyState } from '../lib/game';
import { buildGeometry } from '../lib/geometry';
import { chipColours } from '../lib/score';

type Tool = 'select' | 'draw' | 'erase';
type Tab = 'assist' | 'rules' | 'path' | 'fog' | 'finish';

interface Drag {
  kind: 'path' | 'area' | 'paint' | 'select' | 'erase' | 'fog';
  cells: Rc[];
  /** A fog drag: lighting (or holding back) cells, or taking them away. */
  on?: boolean;
}

const NO_ASSIST: AssistState = {
  suggestions: [],
  note: null,
  busy: null,
  message: null,
  progress: null,
};

export function BuilderPage() {
  const { id = '' } = useParams();
  const q = useQuery({
    queryKey: ['build', id],
    queryFn: () => api<Draft>(`/api/builds/${id}`),
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });
  if (q.isPending) return <Splash />;
  if (q.isError || !q.data)
    return (
      <div className="mx-auto flex max-w-[480px] flex-col gap-3 px-4 py-10">
        <h1 className="m-0 text-[20px] font-bold">No such draft</h1>
        <Link to="/build?tab=build">Back to your drafts</Link>
      </div>
    );
  if (q.data.status === 'ready') return <ReadyNotice draft={q.data} />;
  return <Builder key={q.data.id} draft={q.data} />;
}

function ReadyNotice({ draft }: { draft: Draft }) {
  const navigate = useNavigate();
  const copy = async () => {
    const r = await api<NewDraftResponse>(`/api/builds/${draft.id}/copy`, { method: 'POST' });
    void navigate(`/build/${r.id}`);
  };
  return (
    <div className="mx-auto flex max-w-[480px] flex-col gap-3 px-4 py-10">
      <h1 className="m-0 text-[20px] font-bold">{draft.title}</h1>
      <p className="m-0 text-[15px] text-muted">
        This puzzle is in the library, so it can’t change any more. Copy it to a new draft to make a
        version of it.
      </p>
      <div className="flex gap-2">
        <Link
          to={`/library/${draft.id}`}
          className="flex h-11 items-center rounded-btn border border-line bg-surface px-4 text-[15px] font-semibold text-ink no-underline"
        >
          Open in the library
        </Link>
        <button
          type="button"
          onClick={() => void copy()}
          className="h-11 rounded-btn border-0 bg-accent px-4 text-[15px] font-semibold text-white"
        >
          Copy to a new draft
        </button>
      </div>
    </div>
  );
}

const withoutSolution = (doc: PuzzleDoc): PuzzleDoc => ({ ...doc, solution: null });

const stepsText = (n: number) => `${n} logical step${n === 1 ? '' : 's'}`;

/** The solution chip (mockup 07): green with a tick for one solution. */
function SolutionChip({ count, complete }: { count: number | null; complete: boolean }) {
  const base =
    'inline-flex h-[30px] items-center gap-1.5 rounded-full px-3 text-[13px] font-bold whitespace-nowrap';
  if (count === null)
    return <span className={`${base} bg-unrated-bg text-unrated-ink`}>Checking…</span>;
  if (!complete) return <span className={`${base} bg-unrated-bg text-unrated-ink`}>Undecided</span>;
  if (count === 0) return <span className={`${base} bg-mistake-bg text-mistake`}>No solution</span>;
  if (count > 1)
    return <span className={`${base} bg-unrated-bg text-unrated-ink`}>2+ solutions</span>;
  return (
    <span className={`${base} bg-solved-bg text-solved`}>
      <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
        <circle cx="8" cy="8" r="7" fill="currentColor" />
        <path
          d="M4.8 8.2l2.1 2.1 4.3-4.4"
          fill="none"
          stroke="#FFFFFF"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      1 solution
    </span>
  );
}

function Builder({ draft }: { draft: Draft }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [t, setT] = useState<Timeline>(() =>
    startTimeline({
      title: draft.title,
      doc: withoutSolution(draft.document),
      builder: draft.builder,
    }),
  );
  const work = t.present;
  const { doc, builder } = work;
  const workRef = useRef(work);
  workRef.current = work;
  const rows = doc.grid.rows;
  const cols = doc.grid.cols;
  const geometry = useMemo(() => buildGeometry(doc), [doc]);
  // A samurai or another shape of 9×9 grids (item 13): named in the header, more room, and on a
  // phone it zooms and pans with the overview map, as when solving.
  const shape = shapeOf(shapeOfGrid(doc.grid));
  const zoomable = Math.max(rows, cols) > 9;
  const blank = useMemo(() => emptyState(geometry), [geometry]);

  /** A step in the history (the builder state keeps only pieces still there). */
  const change = useCallback(
    (f: (w: Work) => Work) =>
      setT((tl) => {
        const next = f(tl.present);
        if (next === tl.present) return tl;
        return commit(tl, { ...next, builder: prune(next.builder, next.doc) });
      }),
    [],
  );
  /** A change without a step of its own (words written from the rules, the working solution). */
  const amendWork = useCallback(
    (f: (w: Work) => Work) => setT((tl) => amend(tl, f(tl.present))),
    [],
  );
  const editDoc = useCallback(
    (f: (d: PuzzleDoc) => PuzzleDoc) => change((w) => byHand(w, f(w.doc))),
    [change],
  );

  const [tool, setTool] = useState<Tool>('select');
  const [itemId, setItemId] = useState<string | null>(null);
  const [variants, setVariants] = useState<Record<string, string>>({});
  const [query, setQuery] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [selection, setSelection] = useState<number[]>([]);
  const [picked, setPicked] = useState<number | null>(null);
  const [drag, setDrag] = useState<Drag | null>(null);
  const [cloneFirst, setCloneFirst] = useState<Rc[] | null>(null);
  const [region, setRegion] = useState(0);
  const [overlay, setOverlay] = useState({ candidates: true, suggestions: true, path: false });
  const [tab, setTab] = useState<Tab>('assist');
  const [pathStep, setPathStep] = useState<number | null>(null);
  const [symmetry, setSymmetry] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);
  const [assist, setAssist] = useState<AssistState>(NO_ASSIST);
  const [candidates, setCandidates] = useState<AutoCandidate[]>([]);
  const [readyJob, setReadyJob] = useState<string | null>(null);
  const [completeJob, setCompleteJob] = useState<string | null>(null);
  const [autoJob, setAutoJob] = useState<string | null>(null);
  const [fogTool, setFogTool] = useState<FogTool | null>(null);
  const [fogKey, setFogKey] = useState<number | null>(null);
  const [fogView, setFogView] = useState<FogView>({ stop: false, step: null });
  const [shrinking, setShrinking] = useState(false);

  const item = itemId ? (paletteItem(itemId) ?? null) : null;
  const variant = item ? variantOf(item, variants[item.id] ?? null) : null;
  const pickedEntry =
    picked !== null && picked < doc.constraints.length ? doc.constraints[picked]! : null;

  // The core reads the grid and pieces only: words (title, rules) don't need a new report.
  const logicDoc = useMemo(
    () => ({ ...doc, meta: { title: '', author: '', rules: '' } }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [doc.grid, doc.givens, doc.constraints, doc.cosmetics, doc.fog],
  );
  const {
    report,
    error: reportError,
    busy: reportBusy,
  } = useBuildReport(logicDoc, overlay.candidates);
  const save = useAutosave(draft.id, work, true);
  const fogState = useFogReport(logicDoc);
  const fog = useMemo(() => fogModel(doc), [doc]);
  const fogReport = fogState.busy ? null : fogState.fog;
  /** The fog changed: the builder keeps the rest of the document. */
  const editFog = useCallback(
    (f: (m: FogModel) => FogModel | null) =>
      editDoc((d) => {
        const m = fogModel(d);
        return m ? withFog(d, f(m)) : d;
      }),
    [editDoc],
  );
  // A new fog solve: the view goes back to the start.
  useEffect(() => setFogView({ stop: false, step: null }), [fogState.fog]);

  // The rules text follows the constraints until the setter writes their own.
  useEffect(() => {
    if (!report || builder.customRules || doc.meta.rules === report.rules) return;
    amendWork((w) => ({ ...w, doc: { ...w.doc, meta: { ...w.doc.meta, rules: report.rules } } }));
  }, [report, builder.customRules, doc.meta.rules, amendWork]);

  // ── Suggestions ───────────────────────────────────────────────────────────
  const seq = useRef(0);
  useEffect(() => {
    // A new grid: earlier suggestions and break-ins no longer apply.
    seq.current++;
    setAssist((a) => ({ ...a, suggestions: [], note: null, busy: null }));
  }, [doc]);

  const runAssist = useCallback(
    async (kind: 'suggest' | 'break-in') => {
      const w = workRef.current;
      const id = ++seq.current;
      setAssist((a) => ({
        ...a,
        message: null,
        busy: kind === 'suggest' ? 'Looking where the logic stalls…' : 'Looking for an opening…',
      }));
      try {
        const req = {
          doc: w.doc,
          types: w.builder.allowed,
          minScore: w.builder.target[0],
          maxScore: w.builder.target[1],
          solution: w.builder.solution,
          seed: Math.floor(Math.random() * 2 ** 31),
          exclude: w.builder.rejected,
        };
        const r = kind === 'suggest' ? await core.suggest(req) : await core.breakIn(req);
        if (id !== seq.current) return;
        setAssist((a) => ({ ...a, suggestions: r.suggestions, note: r.note, busy: null }));
        const sol = r.solution;
        if (sol && JSON.stringify(sol) !== JSON.stringify(w.builder.solution)) {
          amendWork((x) => ({ ...x, builder: { ...x.builder, solution: sol } }));
        }
      } catch (err) {
        if (id === seq.current)
          setAssist((a) => ({ ...a, busy: null, note: (err as Error).message }));
      }
    },
    [amendWork],
  );

  const allowedKey = builder.allowed.join(',');
  const targetKey = builder.target.join(',');
  useEffect(() => {
    if (builder.level !== 'semi' || !overlay.suggestions || reportBusy) return;
    if (!report?.stall || report.solutionCount === 0 || !allowedKey) return;
    const timer = setTimeout(() => void runAssist('suggest'), 300);
    return () => clearTimeout(timer);
  }, [report, reportBusy, builder.level, overlay.suggestions, allowedKey, targetKey, runAssist]);

  const accept = (s: Suggestion, edit = false) => {
    const index = workRef.current.doc.constraints.length;
    change((w) => {
      if (s.entry) return byApp(w, addEntry(w.doc, s.entry), [entryKey(s.entry)]);
      if (s.given)
        return byApp(w, setGivens(w.doc, [s.given.cell], s.given.value), [givenKey(s.given.cell)]);
      return w;
    });
    if (edit && s.entry) {
      setTool('select');
      setItemId(null);
      setPicked(index);
    }
  };

  const reject = (s: Suggestion) => {
    amendWork((w) => ({
      ...w,
      builder: { ...w.builder, rejected: [...w.builder.rejected, s.entry ?? s.given].slice(-200) },
    }));
    setAssist((a) => ({ ...a, suggestions: a.suggestions.filter((x) => x !== s) }));
  };

  const tidy = async () => {
    const w = workRef.current;
    const pieces = removablePieces(w.doc, w.builder);
    if (pieces.constraints.length === 0 && pieces.givens.length === 0) {
      setAssist((a) => ({
        ...a,
        message: 'Nothing the app added is left to tidy; your own pieces stay as they are.',
      }));
      return;
    }
    setAssist((a) => ({ ...a, busy: 'Tidying…', message: null }));
    try {
      const r = await core.tidy({ doc: w.doc, ...pieces });
      const n = r.removedConstraints.length + r.removedGivens.length;
      if (n > 0)
        change((x) => ({
          ...x,
          doc: { ...x.doc, constraints: r.doc.constraints, givens: r.doc.givens },
        }));
      setAssist((a) => ({
        ...a,
        busy: null,
        message:
          n > 0
            ? `Tidy took off ${n} piece${n === 1 ? '' : 's'} the puzzle didn’t need.`
            : 'Every piece the app added is needed.',
      }));
    } catch (err) {
      setAssist((a) => ({ ...a, busy: null, message: (err as Error).message }));
    }
  };

  // ── Worker jobs: Complete, Automatic, Mark ready ──────────────────────────
  const complete = async () => {
    const w = workRef.current;
    if (w.builder.allowed.length === 0) {
      setAssist((a) => ({ ...a, message: 'Choose what the app may add first.' }));
      return;
    }
    try {
      const jobId = await startJob(`/api/builds/${draft.id}/complete`, {
        document: w.doc,
        types: w.builder.allowed,
        minScore: w.builder.target[0],
        maxScore: w.builder.target[1],
        solution: w.builder.solution,
      });
      setAssist((a) => ({ ...a, progress: 0, message: null }));
      setCompleteJob(jobId);
    } catch (err) {
      setAssist((a) => ({ ...a, message: (err as Error).message }));
    }
  };
  const completeStatus = useBuildJob<CompleteResult>(completeJob);
  useEffect(() => {
    const s = completeStatus.data;
    if (!s || !completeJob) return;
    if (s.state === 'done' && s.result) {
      const r = s.result;
      const keys = [
        ...r.addedConstraints.map((i) => entryKey(r.doc.constraints[i]!)),
        ...r.addedGivens.map(givenKey),
      ];
      change((w) =>
        byApp(
          { ...w, builder: { ...w.builder, solution: r.doc.solution ?? w.builder.solution } },
          { ...w.doc, constraints: r.doc.constraints, givens: r.doc.givens },
          keys,
        ),
      );
      setCompleteJob(null);
      setAssist((a) => ({
        ...a,
        progress: null,
        message: `Complete added ${keys.length} piece${keys.length === 1 ? '' : 's'}: one solution, score ${r.score.score.toFixed(1)}${r.inRange ? '' : ' (the closest it found to the target)'}.`,
      }));
    } else if (s.state === 'failed') {
      setCompleteJob(null);
      setAssist((a) => ({ ...a, progress: null, message: s.error }));
    } else {
      setAssist((a) => ({ ...a, progress: s.progress }));
    }
  }, [completeStatus.data, completeJob, change]);

  const generate = async () => {
    const w = workRef.current;
    const global = w.doc.constraints.map((e) => e.type).filter((x) => AUTO_GLOBALS.includes(x));
    try {
      const jobId = await startJob('/api/builds/auto', {
        size: w.doc.grid.rows,
        irregular: Boolean(w.doc.grid.regions),
        types: w.builder.allowed.filter((x) => x !== 'given'),
        global,
        minScore: w.builder.target[0],
        maxScore: w.builder.target[1],
        symmetry,
      });
      setCandidates([]);
      setAssist((a) => ({ ...a, progress: 0, message: null }));
      setAutoJob(jobId);
    } catch (err) {
      setAssist((a) => ({ ...a, message: (err as Error).message }));
    }
  };
  const autoStatus = useBuildJob<AutoResult>(autoJob);
  useEffect(() => {
    const s = autoStatus.data;
    if (!s || !autoJob) return;
    if (s.state === 'done' && s.result) {
      setCandidates(s.result.candidates);
      setAutoJob(null);
      setAssist((a) => ({ ...a, progress: null }));
    } else if (s.state === 'failed') {
      setAutoJob(null);
      setAssist((a) => ({ ...a, progress: null, message: s.error }));
    } else {
      setAssist((a) => ({ ...a, progress: s.progress }));
    }
  }, [autoStatus.data, autoJob]);

  const chooseCandidate = (c: AutoCandidate) =>
    change((w) => ({
      ...w,
      doc: {
        ...c.doc,
        meta: { ...w.doc.meta, rules: c.doc.meta.rules },
        solution: null,
      },
      builder: {
        ...w.builder,
        added: allKeys(c.doc),
        generated: true,
        customRules: false,
        solution: c.doc.solution ?? null,
      },
    }));

  const markReady = async () => {
    setBanner(null);
    try {
      await save.flush();
      setReadyJob(await startJob(`/api/builds/${draft.id}/ready`));
    } catch (err) {
      setBanner((err as Error).message);
    }
  };
  const readyStatus = useBuildJob<ReadyResult>(readyJob);
  useEffect(() => {
    const s = readyStatus.data;
    if (!s || !readyJob) return;
    if (s.state === 'done') {
      void qc.invalidateQueries({ queryKey: ['library'] });
      void qc.invalidateQueries({ queryKey: ['builds'] });
      void qc.removeQueries({ queryKey: ['build', draft.id] });
      void navigate(`/library/${draft.id}`, { replace: true });
    } else if (s.state === 'failed') {
      setReadyJob(null);
      setBanner(s.error ?? 'It could not be marked ready.');
    }
  }, [readyStatus.data, readyJob, qc, navigate, draft.id]);

  const shrinkLight = async () => {
    const w = workRef.current;
    setShrinking(true);
    try {
      const r = await core.fogShrink({ doc: w.doc, seconds: 30 });
      if (r.removed > 0) editFog((m) => ({ ...m, visible: r.visible }));
      setBanner(
        r.removed > 0
          ? `The light lost ${r.removed} cell${r.removed === 1 ? '' : 's'} it didn’t need.`
          : r.timedOut
            ? 'Shrinking the light ran out of time.'
            : 'Every lit cell is needed.',
      );
    } catch (err) {
      setBanner((err as Error).message);
    } finally {
      setShrinking(false);
    }
  };

  const testSolve = async () => {
    setBanner(null);
    try {
      await save.flush();
      void navigate(`/puzzle/${draft.id}`);
    } catch (err) {
      setBanner((err as Error).message);
    }
  };

  // ── Drawing ───────────────────────────────────────────────────────────────
  const addAndPick = (e: ConstraintEntry) => {
    const index = workRef.current.doc.constraints.length;
    editDoc((d) => addEntry(d, e));
    setPicked(index);
  };

  const nearEdge = (pt: Pt) => {
    const fr = pt.r - Math.floor(pt.r);
    const fc = pt.c - Math.floor(pt.c);
    return Math.min(fr, 1 - fr, fc, 1 - fc) < 0.2;
  };

  /** The piece at a point: an outside clue, an edge clue near an edge, a quadruple near a
   * corner, else the cell's lines and cages (tapping again goes to the next one). */
  const pieceAt = (pt: Pt, cell: number): number | null => {
    if (cell < 0) {
      const clue = outsideAt(pt, rows, cols, true);
      const i = clue ? clueEntryAt(doc, clue) : -1;
      return i >= 0 ? i : null;
    }
    if (nearEdge(pt)) {
      const pair = edgeAt(pt, rows, cols);
      const i = pair ? edgeEntryAt(doc, pair[0], pair[1]) : -1;
      if (i >= 0) return i;
      const corner = cornerAt(pt, rows, cols);
      if (corner) {
        const q = doc.constraints.findIndex(
          (e) =>
            e.type === 'quadruple' &&
            Array.isArray(e.cells) &&
            (e.cells as Rc[]).length === corner.length &&
            corner.every((c) => (e.cells as Rc[]).some((x) => same(x, c))),
        );
        if (q >= 0) return q;
      }
    }
    const list = entriesAtCell(doc, cellOfPt(pt));
    if (list.length === 0) return null;
    const k = picked === null ? -1 : list.indexOf(picked);
    return list[(k + 1) % list.length]!;
  };

  const eraseAt = (pt: Pt, cell: number, onlyCells = false) => {
    const rc = cellOfPt(pt);
    if (cell >= 0 && givenAt(doc, rc) !== null) {
      editDoc((d) => setGivens(d, [rc], null));
      return;
    }
    if (onlyCells && cell < 0) return;
    const i = onlyCells ? (entriesAtCell(doc, rc)[0] ?? null) : pieceAt(pt, cell);
    if (i !== null) {
      editDoc((d) => removeEntries(d, [i]));
      setPicked(null);
    }
  };

  const placeEdge = (pair: [Rc, Rc]) => {
    if (!variant) return;
    const i = edgeEntryAt(doc, pair[0], pair[1]);
    const fresh = edgeEntry(variant, pair);
    if (i >= 0) {
      const old = doc.constraints[i]!;
      const sameKind =
        old.type === fresh.type &&
        Object.entries(variant.params ?? {}).every(([k, v]) => old[k] === v);
      if (sameKind) {
        editDoc((d) => removeEntries(d, [i]));
        setPicked(null);
      } else {
        editDoc((d) => updateEntry(d, i, fresh));
        setPicked(i);
      }
      return;
    }
    addAndPick(fresh);
  };

  const placeClue = (clue: Rc, little: boolean) => {
    if (!variant) return;
    const i = clueEntryAt(doc, clue);
    if (i >= 0) {
      const old = doc.constraints[i]!;
      if (little && old.type === 'little-killer') {
        // Tapping again turns it to the next direction into the grid.
        const dirs = diagonalsFrom(clue, rows, cols);
        const now = JSON.stringify(old.direction);
        const k = dirs.findIndex((d) => JSON.stringify(d) === now);
        editDoc((d) => updateEntry(d, i, { ...old, direction: dirs[(k + 1) % dirs.length] }));
      } else if (old.type !== variant.type) {
        editDoc((d) => updateEntry(d, i, outsideEntry(variant, clue, rows, cols)));
      }
      setPicked(i);
      return;
    }
    addAndPick(outsideEntry(variant, clue, rows, cols));
  };

  const cellsToIndices = (cells: Rc[]) =>
    cells.map(([r, c]) => geometry.index[r * cols + c] ?? -1).filter((i) => i >= 0);

  const onDown = (cell: number, e: ReactPointerEvent<SVGSVGElement>) => {
    const pt = pointOf(e.currentTarget, e.clientX, e.clientY);
    if (!pt) return;
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      // Not every pointer can be captured (synthetic events in tests).
    }
    const rc = cellOfPt(pt);
    const inGrid = cell >= 0;
    if (fogTool && fog) {
      if (!inGrid) return;
      setSelection([]);
      if (fogTool === 'key' && fogKey === null) {
        setDrag({ kind: 'fog', cells: [rc] });
        return;
      }
      const list =
        fogTool === 'light'
          ? fog.visible
          : fogTool === 'held'
            ? fog.held
            : (fog.keys[fogKey!]?.light ?? []);
      setDrag({ kind: 'fog', cells: [rc], on: !hasRc(list, rc) });
      return;
    }
    if (tool === 'erase') {
      eraseAt(pt, cell);
      setDrag({ kind: 'erase', cells: [rc] });
      return;
    }
    if (tool === 'select' || !item || !variant) {
      setPicked(pieceAt(pt, cell));
      if (inGrid) {
        const add = e.shiftKey || e.ctrlKey || e.metaKey;
        setSelection((s) =>
          add ? (s.includes(cell) ? s.filter((x) => x !== cell) : [...s, cell]) : [cell],
        );
        setDrag({ kind: 'select', cells: [rc] });
      } else {
        setSelection([]);
      }
      return;
    }
    setSelection([]);
    switch (item.gesture) {
      case 'line':
      case 'arrow':
        if (inGrid) setDrag({ kind: 'path', cells: [rc] });
        break;
      case 'cage':
      case 'clone':
        if (inGrid) setDrag({ kind: 'area', cells: [rc] });
        break;
      case 'region':
        if (inGrid) setDrag({ kind: 'paint', cells: [rc] });
        break;
      case 'cell':
        if (inGrid) editDoc((d) => toggleCellMark(d, variant, rc));
        break;
      case 'edge': {
        const pair = edgeAt(pt, rows, cols);
        if (pair) placeEdge(pair);
        break;
      }
      case 'outside': {
        const clue = outsideAt(pt, rows, cols);
        if (clue) placeClue(clue, false);
        break;
      }
      case 'little-killer': {
        const clue = outsideAt(pt, rows, cols, true);
        if (clue && diagonalsFrom(clue, rows, cols).length > 0) placeClue(clue, true);
        break;
      }
      case 'corner': {
        const cells = cornerAt(pt, rows, cols);
        if (cells) addAndPick({ type: 'quadruple', cells, digits: [] });
        break;
      }
      case 'square': {
        const sq = inGrid ? squareEntry(rc, rows, cols) : null;
        if (sq) addAndPick(sq);
        break;
      }
      default:
        break;
    }
  };

  const onMove = (cell: number, e: ReactPointerEvent<SVGSVGElement>) => {
    if (!drag || cell < 0) return;
    const pt = pointOf(e.currentTarget, e.clientX, e.clientY);
    if (!pt) return;
    const rc = cellOfPt(pt);
    if (drag.kind === 'path') {
      const cells = extendPath(drag.cells, rc);
      if (cells !== drag.cells) setDrag({ ...drag, cells });
    } else if (drag.kind === 'erase') {
      if (!drag.cells.some((c) => same(c, rc))) {
        eraseAt(pt, cell, true);
        setDrag({ ...drag, cells: [...drag.cells, rc] });
      }
    } else {
      const cells = extendArea(drag.cells, rc);
      if (cells !== drag.cells) {
        setDrag({ ...drag, cells });
        if (drag.kind === 'select') setSelection(cellsToIndices(cells));
      }
    }
  };

  const onUp = () => {
    const d = drag;
    setDrag(null);
    if (d?.kind === 'fog') {
      if (fogTool === 'key' && fogKey === null) {
        const index = fog?.keys.length ?? 0;
        editFog((m) => ({ ...m, keys: [...m.keys, { when: d.cells, light: [] }] }));
        setFogKey(index);
      } else if (fogTool === 'light') {
        editFog((m) => ({ ...m, visible: paintCells(m.visible, d.cells, d.on ?? true) }));
      } else if (fogTool === 'held') {
        editFog((m) => ({ ...m, held: paintCells(m.held, d.cells, d.on ?? true) }));
      } else if (fogTool === 'key' && fogKey !== null) {
        const k = fogKey;
        editFog((m) => ({
          ...m,
          keys: m.keys.map((x, i) =>
            i === k ? { ...x, light: paintCells(x.light, d.cells, d.on ?? true) } : x,
          ),
        }));
      }
      return;
    }
    if (!d || !item || !variant) return;
    if (d.kind === 'path') {
      const e = item.gesture === 'arrow' ? arrowEntry(d.cells) : lineEntry(variant, d.cells);
      if (e) addAndPick(e);
    } else if (d.kind === 'area') {
      if (item.gesture === 'clone') {
        if (!cloneFirst) {
          setCloneFirst(d.cells);
        } else {
          const e = cloneEntry(cloneFirst, d.cells);
          setCloneFirst(null);
          if (e) addAndPick(e);
          else setBanner('The copy needs as many cells as the first area.');
        }
      } else {
        const e = areaEntry(variant, d.cells);
        if (e) addAndPick(e);
      }
    } else if (d.kind === 'paint') {
      editDoc((x) => d.cells.reduce((acc, rc) => paintRegion(acc, rc, region), x));
    }
  };

  // Keyboard: digits place givens on the selected cells, Delete clears, Ctrl+Z / Ctrl+Y.
  const selectedCells = useMemo(
    () => selection.map((i) => geometry.cells[i]).filter((rc): rc is Rc => Boolean(rc)),
    [selection, geometry],
  );
  const keyState = useRef({ picked, selectedCells, digits: geometry.digits });
  keyState.current = { picked, selectedCells, digits: geometry.digits };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.closest('input, textarea, select, [contenteditable="true"]')) return;
      const k = keyState.current;
      const ctrl = e.ctrlKey || e.metaKey;
      if (ctrl && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        setT((tl) => (e.shiftKey ? redo(tl) : undo(tl)));
        return;
      }
      if (ctrl && e.key.toLowerCase() === 'y') {
        e.preventDefault();
        setT(redo);
        return;
      }
      if (ctrl || e.altKey) return;
      if (e.key === 'Escape') {
        setDrag(null);
        setFogKey(null);
        setCloneFirst(null);
        setPicked(null);
        setSelection([]);
        return;
      }
      if (e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault();
        if (k.picked !== null) {
          const i = k.picked;
          editDoc((d) => removeEntries(d, [i]));
          setPicked(null);
        } else if (k.selectedCells.length) {
          editDoc((d) => setGivens(d, k.selectedCells, null));
        }
        return;
      }
      const digit = k.digits.find((d) => d.length === 1 && d.toLowerCase() === e.key.toLowerCase());
      if (digit && k.selectedCells.length) {
        e.preventDefault();
        editDoc((d) => setGivens(d, k.selectedCells, digit));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editDoc]);

  // ── What the grid shows ───────────────────────────────────────────────────
  const hasPieces = doc.givens.length > 0 || doc.constraints.length > 0;
  const shaded =
    report?.stall && hasPieces
      ? report.stall.area.filter((rc) => !selection.includes(geometry.index[rc[0] * cols + rc[1]]!))
      : [];
  const pathCells =
    (tab === 'path' || overlay.path) && pathStep !== null
      ? (report?.steps[pathStep]?.cells ?? null)
      : null;
  const hint = useMemo(
    () => new Set(pathCells ? cellsToIndices(pathCells) : []),
    // cellsToIndices reads the geometry only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pathCells, geometry],
  );
  const marks: Marked[] = [];
  if (builder.level !== 'manual' && overlay.suggestions) {
    assist.suggestions.forEach((s, i) =>
      marks.push({ entry: s.entry, given: s.given, cells: s.cells, letter: 'ABCDE'[i] }),
    );
  }
  if (pickedEntry) marks.push({ entry: pickedEntry, given: null, cells: [] });
  const regionCells =
    item?.gesture === 'region' && !drag
      ? (doc.grid.regions?.[region] ?? [])
      : drag?.kind === 'paint'
        ? drag.cells
        : [];
  const needsRing =
    item?.gesture === 'outside' ||
    item?.gesture === 'little-killer' ||
    doc.constraints.some((e) => Array.isArray(e.clue));
  const view = useMemo(() => {
    const base = gridFrame(doc);
    if (!needsRing) return base;
    const left = Math.max(-base.x, CELL + 4);
    const top = Math.max(-base.y, CELL + 4);
    const right = Math.max(base.w + base.x - cols * CELL, CELL + 4);
    const bottom = Math.max(base.h + base.y - rows * CELL, CELL + 4);
    return { x: -left, y: -top, w: cols * CELL + left + right, h: rows * CELL + top + bottom };
  }, [doc, needsRing, rows, cols]);
  // The builder shows every clue: its own fog shading stands for the fog, which the grid would
  // otherwise draw as the player sees it.
  const gridDoc = useMemo(() => (doc.fog ? { ...doc, fog: null } : doc), [doc]);
  const fogShown = (() => {
    if (!fog) return null;
    const cells = geometry.cells;
    const at = fogReport?.litAt ?? [];
    const placedUpTo = (k: number) =>
      fogReport?.steps.slice(0, k + 1).flatMap((s) => s.placed) ?? [];
    if (fogReport && fogView.step !== null && fogReport.steps[fogView.step]) {
      const k = fogView.step;
      return {
        dark: cells.filter((_, i) => at[i] === null || at[i] === undefined || at[i] > k + 1),
        focus: fogReport.steps[k]!.cells,
        placed: placedUpTo(k),
      };
    }
    if (fogReport && fogView.stop && fogReport.stop) {
      return {
        dark: cells.filter((_, i) => at[i] === null || at[i] === undefined),
        focus: fogReport.stop.cells,
        placed: placedUpTo(fogReport.steps.length - 1),
      };
    }
    return { dark: cells.filter((rc) => !hasRc(fog.visible, rc)), focus: [], placed: [] };
  })();
  const gridState = useMemo(() => {
    if (!fogShown?.placed.length) return blank;
    const cellsNow = blank.cells.map((c) => ({ ...c }));
    for (const [rc, d] of fogShown.placed) {
      const i = geometry.index[rc[0] * cols + rc[1]] ?? -1;
      if (i >= 0) cellsNow[i] = { ...cellsNow[i]!, value: d };
    }
    return { ...blank, cells: cellsNow };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blank, fogReport, fogView, geometry, cols]);
  const selectionSet = useMemo(() => {
    const s = new Set(selection);
    if (drag?.kind === 'area' || drag?.kind === 'fog')
      for (const i of cellsToIndices(drag.cells)) s.add(i);
    if (cloneFirst) for (const i of cellsToIndices(cloneFirst)) s.add(i);
    return s;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection, drag, cloneFirst, geometry]);

  const status = (() => {
    if (reportError) return `The solver can’t read this grid yet: ${reportError}`;
    if (!report) return 'Checking…';
    const parts: string[] = [];
    if (!report.complete) parts.push('The search ran out of time counting solutions');
    else if (report.solutionCount === 0) parts.push('No solution');
    else if (report.solutionCount === 1) parts.push('1 solution');
    else parts.push('More than one solution');
    if (report.solutionCount === 0 && report.conflicts[0])
      parts.push(`the givens break a rule (${report.conflicts[0].rule})`);
    if (report.steps.length) parts.push(stepsText(report.steps.length));
    if (report.stall && hasPieces)
      parts.push(
        `the logic stalls at step ${report.stall.step}, in ${report.stall.house} (shaded)`,
      );
    else if (report.solved) parts.push('solved by logic');
    return parts.join(' · ');
  })();

  const canMarkReady =
    report !== null &&
    !reportBusy &&
    !reportError &&
    report.solutionCount === 1 &&
    report.complete &&
    report.score !== null &&
    (!doc.fog || fogReport?.solved === true) &&
    !readyJob;
  const readyNote = !report
    ? ''
    : report.solutionCount !== 1
      ? 'Mark ready needs exactly one solution.'
      : !report.score
        ? 'Mark ready needs the logic to finish the puzzle.'
        : doc.fog && !fogReport?.solved
          ? 'Mark ready needs the puzzle to solve through its fog.'
          : '';
  const fogChip = !doc.fog
    ? null
    : !fogReport
      ? { text: 'Through the fog…', solved: false }
      : fogReport.solved
        ? { text: 'Solves through the fog', solved: true }
        : fogReport.note
          ? null
          : { text: `Through the fog: stops after ${fogReport.steps.length} steps`, solved: false };
  const headerScore = (doc.fog && fogReport?.solved ? fogReport.score : null) ?? report?.score;

  const setLevel = (level: HelpLevel) =>
    amendWork((w) => ({ ...w, builder: { ...w.builder, level } }));
  const setMeta = (patch: Partial<PuzzleDoc['meta']>, extra?: Partial<Work['builder']>) =>
    change((w) => ({
      ...w,
      doc: { ...w.doc, meta: { ...w.doc.meta, ...patch } },
      builder: { ...w.builder, ...extra },
    }));

  const tool3 = (key: Tool, label: string) => (
    <button
      key={key}
      type="button"
      aria-pressed={tool === key}
      onClick={() => {
        setTool(key);
        if (key !== 'draw') {
          setItemId(null);
          setFogTool(null);
          setFogKey(null);
        }
        setDrag(null);
      }}
      className={`h-[34px] rounded-[8px] border-0 px-3 text-[13px] ${tool === key ? 'bg-surface font-semibold text-ink shadow-[0_1px_2px_rgba(31,30,28,0.14)]' : 'bg-transparent font-medium text-tool-ink'}`}
    >
      {label}
    </button>
  );
  const pill = (on: boolean, label: string, onClick: () => void) => (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={`h-[34px] rounded-full px-3 text-[13px] whitespace-nowrap ${on ? 'border-0 bg-ink font-semibold text-white' : 'border border-line bg-surface font-medium text-ink'}`}
    >
      {label}
    </button>
  );
  const iconBtn =
    'flex h-10 w-10 items-center justify-center rounded-btn border border-line bg-surface p-0 text-ink disabled:opacity-40';

  return (
    <div className="flex min-h-dvh flex-col bg-bg text-ink lg:h-dvh">
      <header className="relative flex min-h-14 shrink-0 flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-line bg-surface px-2 py-1.5 lg:flex-nowrap lg:px-4">
        <Link
          to="/build?tab=build"
          aria-label="Back to drafts"
          className="flex h-10 w-10 items-center justify-center rounded-btn text-ink"
        >
          <BackIcon />
        </Link>
        <div className="flex min-w-0 flex-1 flex-col gap-px">
          <span className="truncate text-[16px] font-bold">{work.title}</span>
          <span className="truncate text-[12px] text-muted">
            Draft · only you can see it ·{' '}
            {shape
              ? `${shape.name}, ${['', 'one', 'two', 'three', 'four', 'five'][doc.grid.subGrids?.length ?? 0]} ${doc.grid.subGrids?.[0]?.rows ?? 9}×${doc.grid.subGrids?.[0]?.rows ?? 9} grids`
              : `${rows}×${cols}`}
            {doc.fog ? ' · Fog' : ''}
            {save.saving ? ' · saving…' : save.error ? ' · not saved' : ''}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <SolutionChip
            count={report ? report.solutionCount : null}
            complete={report?.complete ?? true}
          />
          {fogChip && (
            <span
              className={`inline-flex h-[30px] items-center rounded-full px-3 text-[13px] font-bold ${fogChip.solved ? 'bg-solved-bg text-solved' : 'bg-warn-bg text-warn-ink'}`}
            >
              {fogChip.text}
            </span>
          )}
          {headerScore && (
            <span
              className="inline-flex h-[30px] items-center rounded-full px-3 text-[13px] font-bold"
              style={chipColours(headerScore.score)}
            >
              Score {headerScore.score.toFixed(1)}
            </span>
          )}
          {report && report.steps.length > 0 && (
            <span className="px-1 text-[13px] text-muted">{stepsText(report.steps.length)}</span>
          )}
          <button
            type="button"
            onClick={() => void testSolve()}
            className="h-10 rounded-btn border border-line bg-surface px-4 text-[14px] font-semibold text-ink"
          >
            Test solve
          </button>
          <button
            type="button"
            disabled={!canMarkReady}
            title={readyNote}
            onClick={() => void markReady()}
            className="h-10 rounded-btn border-0 bg-accent px-4 text-[14px] font-semibold text-white disabled:opacity-45"
          >
            {readyJob ? 'Checking…' : 'Mark ready'}
          </button>
          <button
            type="button"
            aria-label="More options"
            onClick={() => setMenuOpen(true)}
            className="flex h-10 w-10 items-center justify-center rounded-btn border-0 bg-transparent p-0 text-ink"
          >
            <DotsIcon />
          </button>
        </div>
        {menuOpen && (
          <Menu
            onClose={() => setMenuOpen(false)}
            actions={[
              {
                key: 'export',
                label: 'Copy SudokuPad link',
                icon: menuIcons.export,
                onSelect: () =>
                  void core
                    .exportLink(doc)
                    .then((link) => navigator.clipboard.writeText(link))
                    .then(() => setBanner('SudokuPad link copied.'))
                    .catch(() => setBanner('The link couldn’t be copied.')),
              },
              {
                key: 'copy',
                label: 'Copy to a new draft',
                icon: <RulesIcon />,
                onSelect: () =>
                  void save
                    .flush()
                    .then(() =>
                      api<NewDraftResponse>(`/api/builds/${draft.id}/copy`, { method: 'POST' }),
                    )
                    .then((r) => navigate(`/build/${r.id}`))
                    .catch((err: Error) => setBanner(err.message)),
              },
              {
                key: 'delete',
                label: 'Delete draft',
                icon: <DeleteIcon />,
                divider: true,
                onSelect: () => {
                  if (!window.confirm(`Delete “${work.title}”? This can’t be undone.`)) return;
                  void api(`/api/builds/${draft.id}`, { method: 'DELETE' })
                    .then(() => qc.invalidateQueries({ queryKey: ['builds'] }))
                    .then(() => navigate('/build?tab=build'))
                    .catch((err: Error) => setBanner(err.message));
                },
              },
            ]}
          />
        )}
      </header>
      {banner && (
        <div
          role="alert"
          className="flex items-center gap-3 border-b border-line bg-note-bg px-4 py-2 text-[14px] text-note-ink"
        >
          <span className="flex-1">{banner}</span>
          <button
            type="button"
            onClick={() => setBanner(null)}
            className="h-8 rounded-[8px] border border-line bg-surface px-3 text-[13px] text-ink"
          >
            OK
          </button>
        </div>
      )}
      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
        <aside
          aria-label="Constraints"
          className="order-3 box-border flex shrink-0 flex-col gap-3 border-t border-line bg-surface p-3.5 lg:order-none lg:w-[248px] lg:overflow-y-auto lg:border-t-0 lg:border-r"
        >
          {fog && (
            <FogTools
              reveal={fog.reveal}
              tool={fogTool}
              onTool={(t) => {
                setFogTool(t);
                setFogKey(null);
                if (t) {
                  setItemId(null);
                  setTool('draw');
                  setPicked(null);
                  setTab('fog');
                }
              }}
            />
          )}
          <Palette
            doc={doc}
            query={query}
            onQuery={setQuery}
            showAll={showAll}
            onShowAll={setShowAll}
            itemId={itemId}
            onItem={(id) => {
              setFogTool(null);
              setFogKey(null);
              setItemId(id);
              setTool(id ? 'draw' : 'select');
              setPicked(null);
              setCloneFirst(null);
            }}
            variant={item ? (variants[item.id] ?? null) : null}
            onVariant={(key) => item && setVariants((v) => ({ ...v, [item.id]: key }))}
            onGlobal={(type, params, on) => editDoc((d) => setGlobal(d, type, params, on))}
            onNegative={(type, values, on) => editDoc((d) => setNegative(d, type, values, on))}
            region={region}
            onRegion={setRegion}
            regionProblem={regionProblem(doc)}
          />
          {cloneFirst && (
            <p role="status" className="m-0 text-[12px] text-muted">
              Now drag the copy: {cloneFirst.length} cell{cloneFirst.length === 1 ? '' : 's'}.
            </p>
          )}
          {pickedEntry && picked !== null && (
            <PieceCard
              key={picked}
              entry={pickedEntry}
              rows={rows}
              cols={cols}
              digits={doc.grid.digits}
              addedByApp={builder.added.includes(entryKey(pickedEntry))}
              onChange={(e) => editDoc((d) => updateEntry(d, picked, e))}
              onDelete={() => {
                editDoc((d) => removeEntries(d, [picked]));
                setPicked(null);
              }}
            />
          )}
        </aside>
        <main className="order-1 flex min-w-0 flex-1 flex-col items-center gap-3 px-3 py-3 lg:order-none lg:overflow-y-auto lg:px-5 lg:py-4">
          <div className="flex max-w-full flex-wrap items-center justify-center gap-2">
            <div
              role="group"
              aria-label="Tool"
              className="flex gap-0.5 rounded-btn bg-segment p-[3px]"
            >
              {tool3('select', 'Select')}
              {tool3('draw', 'Draw')}
              {tool3('erase', 'Erase')}
            </div>
            <button
              type="button"
              aria-label="Undo"
              disabled={t.past.length === 0}
              onClick={() => setT(undo)}
              className={iconBtn}
            >
              <UndoIcon />
            </button>
            <button
              type="button"
              aria-label="Redo"
              disabled={t.future.length === 0}
              onClick={() => setT(redo)}
              className={iconBtn}
            >
              <RedoIcon />
            </button>
            <span aria-hidden="true" className="mx-1 hidden h-6 w-px bg-line sm:block" />
            {pill(overlay.candidates, 'True candidates', () =>
              setOverlay((o) => ({ ...o, candidates: !o.candidates })),
            )}
            {pill(overlay.suggestions, 'Suggestions', () =>
              setOverlay((o) => ({ ...o, suggestions: !o.suggestions })),
            )}
            {pill(overlay.path, 'Solve path', () => {
              setOverlay((o) => ({ ...o, path: !o.path }));
              if (!overlay.path) setTab('path');
            })}
          </div>
          <div
            className={`w-full ${shape ? 'max-w-[760px] lg:max-w-[min(760px,calc(100dvh-170px))]' : 'max-w-[600px] lg:max-w-[min(600px,calc(100dvh-200px))]'}`}
          >
            <ZoomView
              key={`${view.x},${view.y},${view.w},${view.h}`}
              frame={view}
              enabled={zoomable}
              maxScale={Math.max(2, Math.max(rows, cols) / 5)}
              onGestureStart={() => setDrag(null)}
              overview={
                doc.grid.subGrids && doc.grid.subGrids.length > 1
                  ? (shown, jump) => (
                      <Overview
                        doc={gridDoc}
                        geometry={geometry}
                        state={gridState}
                        frame={view}
                        view={shown}
                        onJump={jump}
                      />
                    )
                  : undefined
              }
            >
              {(zoomed) => (
                <Grid
                  doc={gridDoc}
                  geometry={geometry}
                  state={gridState}
                  selection={selectionSet}
                  hint={hint}
                  markStyle="sudokupad"
                  view={zoomed ?? view}
                  label="Puzzle being built"
                  onPointerDown={onDown}
                  onPointerMove={onMove}
                  onPointerUp={onUp}
                  underlay={shaded.length > 0 ? <StallShade cells={shaded} /> : null}
                  overlay={
                    <>
                      {overlay.candidates && report?.trueCandidates && (
                        <Candidates
                          geometry={geometry}
                          candidates={report.trueCandidates}
                          hide={(i) => geometry.givens[i] !== null}
                        />
                      )}
                      {fog && fogShown && (
                        <FogOverlay
                          dark={fogShown.dark}
                          held={fog.reveal === 'held' ? fog.held : []}
                          keys={fog.keys}
                          selected={fogKey}
                          focus={fogShown.focus}
                        />
                      )}
                      <RegionCells cells={regionCells} />
                      <MarkedPieces doc={doc} geometry={geometry} marks={marks} />
                      {drag?.kind === 'path' && (
                        <DrawingPath path={drag.cells} arrow={item?.gesture === 'arrow'} />
                      )}
                    </>
                  }
                />
              )}
            </ZoomView>
          </div>
          <p
            role="status"
            aria-label="Puzzle status"
            className="m-0 text-center text-[13px] text-muted"
          >
            {status}
          </p>
          {selection.length > 0 && tool === 'select' && (
            <div
              role="group"
              aria-label="Givens"
              className="grid w-full max-w-[600px] gap-1.5"
              style={{
                gridTemplateColumns: `repeat(${geometry.digits.length <= 9 ? geometry.digits.length + 1 : 9}, minmax(0, 1fr))`,
              }}
            >
              {geometry.digits.map((d) => (
                <button
                  key={d}
                  type="button"
                  aria-label={`Given ${d}`}
                  onClick={() => editDoc((x) => setGivens(x, selectedCells, d))}
                  className="h-11 rounded-[12px] border border-line bg-surface text-[18px] font-medium text-ink"
                >
                  {d}
                </button>
              ))}
              <button
                type="button"
                aria-label="Clear givens"
                onClick={() => editDoc((x) => setGivens(x, selectedCells, null))}
                className="h-11 rounded-[12px] border border-line bg-surface text-[13px] font-medium text-tool-ink"
              >
                Clear
              </button>
            </div>
          )}
        </main>
        <aside
          aria-label="Assist"
          className="order-2 box-border flex shrink-0 flex-col border-t border-line bg-surface lg:order-none lg:w-[380px] lg:border-t-0 lg:border-l"
        >
          <div
            role="tablist"
            aria-label="Panel"
            className="flex shrink-0 gap-1 overflow-x-auto border-b border-row-line px-3"
          >
            {(
              [
                ['assist', 'Assist'],
                ['rules', 'Rules'],
                ['path', 'Solve path'],
                ['fog', 'Fog'],
                ['finish', 'Finish message'],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={tab === key}
                onClick={() => setTab(key)}
                className={`h-[46px] shrink-0 border-0 border-b-2 border-solid bg-transparent px-2.5 text-[14px] ${tab === key ? 'border-accent font-bold text-ink' : 'border-transparent font-medium text-subtle'}`}
              >
                {label}
              </button>
            ))}
          </div>
          <div role="tabpanel" className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 py-3.5">
            {tab === 'assist' && (
              <AssistPanel
                level={builder.level}
                onLevel={setLevel}
                allowed={builder.allowed}
                onAllowed={(allowed) =>
                  amendWork((w) => ({ ...w, builder: { ...w.builder, allowed } }))
                }
                target={builder.target}
                onTarget={(target) =>
                  amendWork((w) => ({ ...w, builder: { ...w.builder, target } }))
                }
                report={report}
                showStall={hasPieces}
                locked={lockedCount(doc, builder)}
                state={assist}
                onAccept={(s) => accept(s)}
                onEdit={(s) => accept(s, true)}
                onReject={reject}
                onSuggest={() => void runAssist('suggest')}
                onBreakIn={() => void runAssist('break-in')}
                onComplete={() => void complete()}
                onTidy={() => void tidy()}
                symmetry={symmetry}
                onSymmetry={setSymmetry}
                onGenerate={() => void generate()}
                candidates={candidates}
                onUse={chooseCandidate}
              />
            )}
            {tab === 'rules' && (
              <RulesPanel
                title={work.title}
                onTitle={(title) =>
                  change((w) => ({
                    ...w,
                    title,
                    doc: { ...w.doc, meta: { ...w.doc.meta, title } },
                  }))
                }
                rules={doc.meta.rules}
                custom={builder.customRules}
                onRules={(rules) => setMeta({ rules }, { customRules: true })}
                onWritten={() =>
                  setMeta({ rules: report?.rules ?? doc.meta.rules }, { customRules: false })
                }
              />
            )}
            {tab === 'path' && (
              <PathPanel report={report} selected={pathStep} onSelect={setPathStep} />
            )}
            {tab === 'fog' && (
              <FogPanel
                model={fog}
                onFog={(on) => {
                  editDoc((d) => withFog(d, on ? NEW_FOG : null));
                  if (on) setFogTool('light');
                  else {
                    setFogTool(null);
                    setFogKey(null);
                  }
                }}
                onReveal={(reveal) => editFog((m) => ({ ...m, reveal }))}
                report={fogState.fog}
                busy={fogState.busy}
                error={fogState.error}
                shrinking={shrinking}
                onShrink={() => void shrinkLight()}
                onClearLight={() => editFog((m) => ({ ...m, visible: [] }))}
                selectedKey={fogKey}
                onSelectKey={(i) => {
                  setFogKey(i);
                  if (i !== null) {
                    setFogTool('key');
                    setItemId(null);
                    setTool('draw');
                  }
                }}
                onRemoveKey={(i) => {
                  editFog((m) => ({ ...m, keys: m.keys.filter((_, k) => k !== i) }));
                  setFogKey(null);
                }}
                onAddKey={() => {
                  setFogKey(null);
                  setFogTool('key');
                  setItemId(null);
                  setTool('draw');
                }}
                view={fogView}
                onView={setFogView}
                plainScore={report?.score?.score ?? null}
                rules={
                  (report?.rules ?? doc.meta.rules)
                    .split(/(?<=\.)\s+/)
                    .filter((x) => /fog/i.test(x))
                    .join(' ') || 'The grid is covered in fog.'
                }
              />
            )}
            {tab === 'finish' && (
              <FinishPanel
                message={doc.meta.finishMessage ?? ''}
                onMessage={(text) => setMeta({ finishMessage: text || null })}
              />
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
